import React, { createContext, useContext, useEffect, useState } from 'react';
import { syncQueuedInspections } from '@/services/syncService';
import { getQueueCount } from '@/services/offlineDB';
import toast from 'react-hot-toast';

interface OfflineContextValue {
  isOnline: boolean;
  queueCount: number;
  refreshQueueCount: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextValue>({
  isOnline: true,
  queueCount: 0,
  refreshQueueCount: async () => {},
});

export function OfflineProvider({ children }: { children: React.ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);

  async function refreshQueueCount() {
    const count = await getQueueCount();
    setQueueCount(count);
  }

  useEffect(() => {
    refreshQueueCount();

    function handleOnline() {
      setIsOnline(true);
      toast.success('Back online — processing photos and syncing inspections...');

      syncQueuedInspections().then(({ synced, failed }) => {
        if (synced > 0) toast.success(`${synced} inspection${synced > 1 ? 's' : ''} submitted successfully`);
        if (failed > 0) toast.error(`${failed} inspection${failed > 1 ? 's' : ''} failed to sync — will retry`);
        refreshQueueCount();
      });
    }

    function handleOffline() {
      setIsOnline(false);
      toast.error('You are offline — inspections will be saved and submitted when reconnected');
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <OfflineContext.Provider value={{ isOnline, queueCount, refreshQueueCount }}>
      {children}
    </OfflineContext.Provider>
  );
}

export const useOffline = () => useContext(OfflineContext);